import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Search, ArrowUpRight, Clock } from "lucide-react";
import { useMemo, useState, useEffect } from "react";

// Compute the next auto-refresh instant (Mon-Fri, HH:20 IST between 10:20 and 15:20).
function nextRefreshIST() {
  const now = new Date();
  // Get current IST components via Intl
  const istParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", hour12: false,
    weekday: "short", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(now).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const wd = istParts.weekday; // Mon..Sun
  const hh = parseInt(istParts.hour, 10);
  const mm = parseInt(istParts.minute, 10);
  const dayIdx = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(wd);
  // Build a Date representing "now" in IST as UTC (approx)
  const y = +istParts.year, mo = +istParts.month, d = +istParts.day;
  const targetHours = [10, 11, 12, 13, 14, 15];
  const isMarketDay = dayIdx >= 1 && dayIdx <= 5;
  let nextH = null;
  if (isMarketDay) {
    for (const h of targetHours) {
      if (hh < h || (hh === h && mm < 20)) { nextH = h; break; }
    }
  }
  // Convert IST date + nextH:20 back to a JS Date (UTC = IST − 5:30)
  const makeDate = (year, month, day, h, m) => {
    // ISO string in IST offset
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00+05:30`;
    return new Date(iso);
  };
  if (nextH !== null) return makeDate(y, mo, d, nextH, 20);
  // Otherwise: next market day 10:20 IST
  let addDays = 1;
  let nd = dayIdx;
  while (true) {
    nd = (nd + 1) % 7;
    if (nd >= 1 && nd <= 5) break;
    addDays += 1;
  }
  const target = new Date(makeDate(y, mo, d, 10, 20).getTime() + addDays * 86400000);
  return target;
}

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, target.getTime() - now);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return { h, m, s, ms };
}

export default function ScannerTable({ data, onRescan, onOpenStock, rescanLoading, todayPanel }) {
  const [query, setQuery] = useState("");
  const [activeLevel, setActiveLevel] = useState("ALL");
  const [nextRef, setNextRef] = useState(() => nextRefreshIST());
  const { h, m, s } = useCountdown(nextRef);

  // Recalculate the target after it passes.
  useEffect(() => {
    const check = setInterval(() => {
      if (nextRef.getTime() <= Date.now()) setNextRef(nextRefreshIST());
    }, 5000);
    return () => clearInterval(check);
  }, [nextRef]);

  const levels = [100, 200, 500, 1000, 5000, 10000];
  const hits = data?.hits || [];

  const filtered = useMemo(() => {
    let list = hits;
    if (activeLevel !== "ALL") list = list.filter((h) => h.level === activeLevel);
    if (query) {
      const q = query.toUpperCase();
      list = list.filter((h) => h.symbol.includes(q) || (h.name || "").toUpperCase().includes(q));
    }
    // Freshest breakouts first (lowest bars_since_signal, then newest signal_time)
    return [...list].sort((a, b) => {
      const ab = a.bars_since_signal ?? 999;
      const bb = b.bars_since_signal ?? 999;
      if (ab !== bb) return ab - bb;
      return (b.signal_time || "").localeCompare(a.signal_time || "");
    });
  }, [hits, query, activeLevel]);

  return (
    <section id="scanner" className="relative py-16 lg:py-24" data-testid="scanner-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#00E5FF] uppercase">01 · Live Scanner</div>
            <h2 className="mt-2 font-display text-4xl lg:text-5xl font-semibold">
              Stocks breaking above <span className="text-[#00E5FF]">psychological levels</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm text-white/55">
              Every 1-hour candle where <span className="text-white">low ≤ level &lt; close</span> is a fresh breakout.
              Filter by level or search for a ticker.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="hidden lg:flex items-center gap-2 pill-btn text-xs" data-testid="next-refresh-chip">
              <Clock className="h-3.5 w-3.5 text-[#00E5FF]" />
              <span className="text-white/60">Auto refresh in</span>
              <span className="font-mono text-white">{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>
              <span className="text-white/40">·</span>
              <span className="font-mono text-white/50">{nextRef.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false })} IST</span>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="scanner-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search RELIANCE, TCS…"
                className="pl-9 pr-4 py-2.5 w-64 rounded-full bg-white/5 border border-white/10 focus:border-[#00E5FF]/50 focus:outline-none text-sm text-white placeholder:text-white/35 font-mono"
              />
            </div>
            <button
              data-testid="scanner-rescan-btn"
              onClick={onRescan}
              disabled={rescanLoading}
              className="pill-btn inline-flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${rescanLoading ? "animate-spin" : ""}`} />
              {rescanLoading ? "Scanning…" : "Rescan now"}
            </button>
          </div>
        </div>

        {/* Level chips */}
        <div className="mb-6 flex flex-wrap gap-2" data-testid="level-filter-chips">
          <button
            data-testid="level-chip-ALL"
            onClick={() => setActiveLevel("ALL")}
            className={`level-chip ${activeLevel === "ALL" ? "active" : ""}`}
          >
            ALL · {hits.length}
          </button>
          {levels.map((l) => {
            const count = hits.filter((h) => h.level === l).length;
            return (
              <button
                key={l}
                data-testid={`level-chip-${l}`}
                onClick={() => setActiveLevel(l)}
                className={`level-chip ${activeLevel === l ? "active" : ""}`}
              >
                ₹{l.toLocaleString("en-IN")} · {count}
              </button>
            );
          })}
        </div>

        {/* Table + Today panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="card-surface rounded-2xl overflow-hidden" data-testid="scanner-table">
            <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/8 text-[10px] font-mono tracking-widest uppercase text-white/45">
              <div className="col-span-2">Symbol</div>
              <div className="col-span-2">Detected (IST)</div>
              <div className="col-span-2 text-right">LTP</div>
              <div className="col-span-1 text-right">Level</div>
              <div className="col-span-1 text-right">SL</div>
              <div className="col-span-1 text-right">Risk %</div>
              <div className="col-span-2 text-center">Status</div>
              <div className="col-span-1 text-right">·</div>
            </div>

            <AnimatePresence>
              {filtered.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="px-6 py-16 text-center text-sm text-white/45"
                  data-testid="scanner-empty-state"
                >
                  {data?.warming_up
                    ? "Warming up — initial scan in progress. Please check back in ~60 seconds."
                    : "No breakouts match your filter. Try widening the level filter or click Rescan."}
                </motion.div>
              )}
            </AnimatePresence>

            {filtered.map((row, i) => {
              const isFresh = (row.bars_since_signal ?? 99) <= 1;
              const detected = row.signal_time ? new Date(row.signal_time) : null;
              const detectedIST = detected
                ? detected.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
                : "—";
              return (
                <motion.button
                  key={`${row.symbol}-${row.signal_time}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02, duration: 0.25 }}
                  onClick={() => onOpenStock?.(row.symbol)}
                  data-testid={`scanner-row-${row.symbol}`}
                  className={`table-row-hover w-full text-left grid grid-cols-12 gap-2 px-5 py-4 border-b border-white/5 last:border-b-0 items-center ${isFresh ? "bg-[#39FF14]/5" : ""}`}
                >
                  <div className="col-span-2 flex flex-col gap-0.5">
                    <span className="ticker-badge">{row.symbol}</span>
                    <span className="text-xs text-white/45 truncate">{row.name}</span>
                  </div>
                  <div className="col-span-2 flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs text-white/70">{detectedIST}</span>
                      {isFresh && (
                        <span className="ticker-badge text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/12 animate-pulse-glow">FRESH</span>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-white/35">
                      {row.bars_since_signal === 0 ? "current 1H candle" : `${row.bars_since_signal}h ago`}
                    </span>
                  </div>
                  <div className="col-span-2 text-right font-mono text-sm text-white">
                    ₹{row.ltp.toLocaleString("en-IN")}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="ticker-badge text-[#00E5FF] border-[#00E5FF]/30 bg-[#00E5FF]/8">
                      ₹{row.level.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="col-span-1 text-right font-mono text-sm text-white/70">
                    ₹{row.stop_loss.toLocaleString("en-IN")}
                  </div>
                  <div className="col-span-1 text-right font-mono text-sm text-[#FF3B30]">
                    −{Math.abs(row.risk_pct).toFixed(2)}%
                  </div>
                  <div className="col-span-2 flex justify-center">
                    {row.still_above_level ? (
                      <span className="ticker-badge text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/8">HOLDING</span>
                    ) : (
                      <span className="ticker-badge text-[#FFB020] border-[#FFB020]/30 bg-[#FFB020]/8">REVERSED</span>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-end text-white/40">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </motion.button>
              );
            })}
          </div>
          {todayPanel}
        </div>
      </div>
    </section>
  );
}
